import classNames from "classnames"
import { animate } from "motion/mini"
import { useReducedMotion } from "motion/react"
import type { AnimationPlaybackControls } from "motion/react"

import Link from "@components/elements/Link"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import { Color } from "@constants/V2/color"
import { getImageDimensionsFromUrl } from "@utils/image"
import { getShadowColorClass } from "@utils/V2/color"
import React, { useEffect, useMemo, useRef, useState } from "react"

export type MarqueeLogo = {
  id: string
  imageUrl: string
  alt: string
  link?: YocoCom.YocoLink
}

type LogoMarqueeProps = {
  logos: MarqueeLogo[]
  backgroundColor: Color
}

const LOGO_HEIGHT = 46
const PIXELS_PER_SECOND = 30
const MINIMUM_COPIES = 2
const FOCUSED_LOGO_EDGE_OFFSET = 16

const getLogoWidth = (imageUrl: string) => {
  const { width, height } = getImageDimensionsFromUrl(imageUrl)
  const intrinsicWidth = Number.parseInt(width)
  const intrinsicHeight = Number.parseInt(height)

  if (!intrinsicWidth || !intrinsicHeight) {
    return undefined
  }

  return Math.round((LOGO_HEIGHT * intrinsicWidth) / intrinsicHeight)
}

const LogoMarquee = ({ logos, backgroundColor }: LogoMarqueeProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const firstCopyRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<AnimationPlaybackControls | null>(null)
  const isHoveredRef = useRef(false)
  const hasFocusWithinRef = useRef(false)
  const [copyCount, setCopyCount] = useState(MINIMUM_COPIES)
  const [loopWidth, setLoopWidth] = useState(0)
  const shouldReduceMotion = useReducedMotion()

  const sizedLogos = useMemo(
    () =>
      logos.map((logo) => ({ ...logo, width: getLogoWidth(logo.imageUrl) })),
    [logos]
  )

  useEffect(() => {
    const container = containerRef.current
    const firstCopy = firstCopyRef.current

    if (!container || !firstCopy) {
      return
    }

    const measureCopies = () => {
      const copyWidth = firstCopy.offsetWidth

      if (copyWidth === 0) {
        return
      }

      setLoopWidth(copyWidth)
      setCopyCount(
        Math.max(
          MINIMUM_COPIES,
          Math.ceil(container.offsetWidth / copyWidth) + 1
        )
      )
    }

    const resizeObserver = new ResizeObserver(measureCopies)
    resizeObserver.observe(container)
    resizeObserver.observe(firstCopy)

    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    const track = trackRef.current

    if (shouldReduceMotion || loopWidth === 0 || !track) {
      return
    }

    const animation = animate(
      track,
      { transform: ["translateX(0px)", `translateX(-${loopWidth}px)`] },
      {
        duration: loopWidth / PIXELS_PER_SECOND,
        ease: "linear",
        repeat: Infinity,
      }
    )
    animationRef.current = animation

    if (isHoveredRef.current || hasFocusWithinRef.current) {
      animation.pause()
    }

    return () => {
      animation.cancel()
      animationRef.current = null
    }
  }, [loopWidth, shouldReduceMotion])

  const syncPlayback = () => {
    if (isHoveredRef.current || hasFocusWithinRef.current) {
      animationRef.current?.pause()
    } else {
      animationRef.current?.play()
    }
  }

  const handlePointerEnter = () => {
    isHoveredRef.current = true
    syncPlayback()
  }

  const handlePointerLeave = () => {
    isHoveredRef.current = false
    syncPlayback()
  }

  const showFocusedLogo = (focusedElement: HTMLElement) => {
    const container = containerRef.current
    const track = trackRef.current
    const animation = animationRef.current

    if (!container || !track || !animation) {
      return
    }

    const offsetInTrack =
      focusedElement.getBoundingClientRect().left -
      track.getBoundingClientRect().left

    animation.time =
      Math.max(0, offsetInTrack - FOCUSED_LOGO_EDGE_OFFSET) / PIXELS_PER_SECOND
    container.scrollLeft = 0
  }

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    hasFocusWithinRef.current = true
    syncPlayback()

    if (event.target.matches(":focus-visible")) {
      showFocusedLogo(event.target)
    }
  }

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    hasFocusWithinRef.current = event.currentTarget.contains(
      event.relatedTarget
    )
    syncPlayback()
  }

  return (
    <div
      ref={containerRef}
      className="relative flex items-center overflow-hidden"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <div ref={trackRef} className="flex w-max">
        {Array.from({ length: copyCount }, (_, copyIndex) => {
          const isDuplicate = copyIndex > 0

          return (
            <div
              key={`logo-copy-${copyIndex}`}
              ref={isDuplicate ? undefined : firstCopyRef}
              aria-hidden={isDuplicate || undefined}
              className="flex shrink-0 items-center gap-64 pr-64"
            >
              {sizedLogos.map((logo) => {
                const logoImage = (
                  <GatsbyStoryblokImage
                    image={logo.imageUrl}
                    alt={logo.alt}
                    width={logo.width}
                    height={LOGO_HEIGHT}
                    loadType="eager"
                    className="max-w-none"
                  />
                )

                return logo.link ? (
                  <Link
                    key={logo.id}
                    link={logo.link}
                    tabIndex={isDuplicate ? -1 : undefined}
                  >
                    {logoImage}
                  </Link>
                ) : (
                  <React.Fragment key={logo.id}>{logoImage}</React.Fragment>
                )
              })}
            </div>
          )
        })}
      </div>
      <div
        className={classNames(
          "absolute left-0 h-full shadow-[0_0_5px_5px]",
          getShadowColorClass(backgroundColor)
        )}
      />
      <div
        className={classNames(
          "absolute right-0 h-full shadow-[0_0_5px_5px]",
          getShadowColorClass(backgroundColor)
        )}
      />
    </div>
  )
}

export default LogoMarquee
